/**
 * Patient portal and smart prescription modules.
 */
import { fetchApi, fetchPatientApi } from '../client';
import { buildQS } from '../helpers';
import type {
  PatientPortalProfile, PatientAppointmentSlot, PatientAppointmentSummary,
  PatientVisitSummary, PatientPrescriptionSummary, AppointmentStatus,
  PrescriptionTemplate, CreateTemplateRequest, FavoriteMedication,
  AddFavoriteMedicationRequest, CommonDiagnosis, EnhancedPrescriptionCreateRequest,
  Prescription,
} from '../../../types';

/**
 * A patient's view of one lab result. Mirrors the backend's PatientLabSummaryView, which is a
 * deliberately narrower projection than the clinician DTO — no doctorSummary, no confidence, no
 * follow-up. Do not widen this to "share" it with the doctor screens.
 */
export interface PatientLabAiSummary {
  labOrderId: string;
  panel: string | null;
  orderedAt: string | null;
  /** False until a clinician has approved the interpretation. */
  summaryAvailable: boolean;
  /** Plain-language text; present only when summaryAvailable is true. */
  patientSummary: string | null;
  awaitingClinicianReview: boolean;
  disclaimer: string;
}

export interface PatientReferralSummary {
  id: string;
  referralNumber: string;
  referringClinicName: string;
  destinationType: 'CLINVEA' | 'EXTERNAL';
  destinationName: string;
  specialty?: string;
  referralReason: string;
  urgency: 'ROUTINE' | 'URGENT' | 'EMERGENCY';
  status: string;
  createdAt: string;
  acceptedAt?: string;
  completedAt?: string;
  declinedAt?: string;
  cancelledAt?: string;
  sharedRecordCategories: string[];
}

export interface PatientTransferSummary {
  id: string;
  transferNumber: string;
  initiatingClinicName: string;
  receivingClinicName: string;
  transferType: string;
  reason: string;
  urgency: 'ROUTINE' | 'URGENT' | 'EMERGENCY';
  status: string;
  requestedAt: string;
  acceptedAt?: string;
  completedAt?: string;
  declinedAt?: string;
  cancelledAt?: string;
  sharedRecordCategories: string[];
}

export const patientPortalModule = {
  auth: {
    login: (email: string, password: string) =>
      fetchPatientApi<{ token: string; patient: PatientPortalProfile }>('/api/patient/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    getCurrentPatient: () =>
      fetchPatientApi<{ patient: PatientPortalProfile }>('/api/patient/auth/me'),
    /** Exchanges the httpOnly patient_refresh_token cookie for a new access token — mirrors
     *  authModule.refresh() on the staff side. */
    refresh: () =>
      fetchPatientApi<{ token: string }>('/api/patient/auth/refresh', { method: 'POST' }),
    logout: () =>
      fetchPatientApi<{ message: string }>('/api/patient/auth/logout', { method: 'POST' }),
    changePassword: (currentPassword: string, newPassword: string) =>
      fetchPatientApi<{ success: boolean }>('/api/patient/auth/password/change', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      }),
    verifyInvitation: (token: string) =>
      fetchPatientApi<{ valid: boolean; email?: string; patientId?: string; expiresAt?: string }>(
        '/api/patient/auth/invitation/verify',
        { method: 'POST', skipAuth: true, body: JSON.stringify({ token }) }
      ),
    /** Self-service resend when the registration link has expired — the (expired) token proves receipt of the original email. */
    resendInvitation: (token: string, redirectUrl: string) =>
      fetchPatientApi<{ message: string; email: string; expiresAt: string }>(
        '/api/patient/auth/invitation/resend',
        { method: 'POST', skipAuth: true, body: JSON.stringify({ token, redirectUrl }) }
      ),
    register: (data: {
      token: string; password: string; name: string; email?: string; mobile?: string;
      dateOfBirth?: string; gender?: string; termsAcceptedAt?: string;
      termsIpAddress?: string; termsUserAgent?: string; termsVersion?: string;
    }) =>
      fetchPatientApi<{ token: string; patient: PatientPortalProfile }>('/api/patient/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  profile: {
    get: () => fetchPatientApi<PatientPortalProfile>('/api/patient/profile'),
    update: (data: Partial<Pick<PatientPortalProfile, 'name' | 'mobile' | 'dateOfBirth' | 'gender'>>) =>
      fetchPatientApi<{ success: boolean }>('/api/patient/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },

  vitals: {
    /** `from`/`to` are ISO date strings (YYYY-MM-DD) — the backend already supports range filtering. */
    get: (patientId: string, visitId?: string, from?: string, to?: string) => {
      const params = new URLSearchParams({ patientId });
      if (visitId) params.append('visitId', visitId);
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      return fetchPatientApi<any[]>(`/api/patient/vitals?${params.toString()}`);
    },
  },

  doctors: {
    getAll: () =>
      fetchPatientApi<Array<{ id: string; name: string; specialization?: string }>>('/api/patient/doctors'),
  },

  /** Read-only history — see PatientReferralTransferHistoryService's class comment on the backend
   *  for the cross-clinic-identity scope limitation (only shows referrals/transfers from clinics
   *  this login is already linked to). */
  referralHistory: {
    list: () => fetchPatientApi<PatientReferralSummary[]>('/api/patient/referrals'),
  },
  transferHistory: {
    list: () => fetchPatientApi<PatientTransferSummary[]>('/api/patient/transfers'),
  },

  appointments: {
    getSlots: (doctorId: string, date: string) =>
      fetchPatientApi<PatientAppointmentSlot[]>(
        `/api/patient/appointments/slots?${new URLSearchParams({ doctorId, date }).toString()}`
      ),
    book: (data: { doctorId: string; date: string; time: string; visitType: 'NEW' | 'FOLLOWUP'; notes?: string }) =>
      fetchPatientApi<{ appointmentId: string; status: AppointmentStatus; tokenNumber?: number }>(
        '/api/patient/appointments',
        { method: 'POST', body: JSON.stringify(data) }
      ),
    list: () => fetchPatientApi<PatientAppointmentSummary[]>('/api/patient/appointments'),
    cancel: (id: string) =>
      fetchPatientApi<{ success: boolean }>(`/api/patient/appointments/${id}/cancel`, {
        method: 'PATCH',
      }),
    /** Book at a clinic not yet linked to this login — found via directory search. Confirmed instantly. */
    bookExternal: (data: {
      targetClinicId: string; doctorId: string; date: string; time: string;
      visitType: 'NEW' | 'FOLLOWUP'; notes?: string;
    }) =>
      fetchPatientApi<{ appointmentId: string; status: AppointmentStatus; tokenNumber?: number }>(
        '/api/patient/appointments/book-external',
        { method: 'POST', body: JSON.stringify(data) }
      ),
  },

  /** Clinics this login has ever booked with (primary clinic first) + booking at a new one. */
  clinics: {
    getLinked: () =>
      fetchPatientApi<Array<{ clinicId: string; clinicName?: string; clinicLogoUrl?: string; isPrimary: boolean }>>(
        '/api/patient/clinics'
      ),
  },

  /** Cross-clinic search — any clinic/doctor on the platform, not just the patient's home clinic. */
  directory: {
    searchClinics: (params: { q?: string; lat?: number; lng?: number; radiusKm?: number }) => {
      const qs = new URLSearchParams();
      if (params.q) qs.append('q', params.q);
      if (params.lat != null) qs.append('lat', String(params.lat));
      if (params.lng != null) qs.append('lng', String(params.lng));
      if (params.radiusKm != null) qs.append('radiusKm', String(params.radiusKm));
      return fetchPatientApi<Array<{
        id: string; name: string; city?: string; state?: string; address?: string;
        phone?: string; logoUrl?: string; distanceKm?: number;
      }>>(`/api/patient/directory/clinics?${qs.toString()}`);
    },
    searchDoctors: (params: { q?: string; clinicId?: string }) => {
      const qs = new URLSearchParams();
      if (params.q) qs.append('q', params.q);
      if (params.clinicId) qs.append('clinicId', params.clinicId);
      return fetchPatientApi<Array<{
        id: string; name: string; specialization?: string; opdFee?: number;
        avatarUrl?: string; bio?: string; clinicId: string; clinicName?: string; clinicCity?: string;
      }>>(`/api/patient/directory/doctors?${qs.toString()}`);
    },
    /** Slots for a doctor at any clinic — resolves clinic from the doctor, not the caller's active clinic. */
    getDoctorSlots: (doctorId: string, date: string) =>
      fetchPatientApi<Array<{ startTime: string; endTime: string }>>(
        `/api/patient/directory/doctors/${doctorId}/slots?date=${date}`
      ),
  },

  /** Messaging with a doctor — restricted server-side to doctors the patient has real appointment history with. */
  messages: {
    getEligibleDoctors: () =>
      fetchPatientApi<Array<{ doctorId: string; name: string; specialization?: string; unreadCount: number }>>(
        '/api/patient/messages/doctors'
      ),
    getThread: (doctorId: string) =>
      fetchPatientApi<Array<{
        id: string; senderType: 'PATIENT' | 'DOCTOR'; subject?: string; body: string;
        isRead: boolean; createdAt: string;
      }>>(`/api/patient/messages/thread/${doctorId}`),
    send: (doctorId: string, data: { subject?: string; body: string }) =>
      fetchPatientApi<{ id: string }>(`/api/patient/messages/thread/${doctorId}`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  /** Family/dependent accounts — search, link, list, unlink, and the consent lifecycle that gates access. */
  dependents: {
    search: (data: { mobile: string; dateOfBirth: string }) =>
      fetchPatientApi<Array<{
        patientId: string; clinicId: string; clinicName?: string; maskedName: string;
        maskedMobile: string; dateOfBirth: string; isMinor: boolean; alreadyLinked: boolean;
      }>>('/api/patient/dependents/search', { method: 'POST', body: JSON.stringify(data) }),
    link: (data: {
      dependentPatientId?: string; dependentClinicId?: string; mobile: string; dateOfBirth: string;
      name?: string; gender?: string; relationshipType: 'CHILD' | 'PARENT' | 'SPOUSE' | 'SIBLING' | 'OTHER';
    }) =>
      fetchPatientApi<{
        dependentLinkId: string; dependentPatientId: string; dependentClinicId: string;
        status: string; isMinor: boolean; message?: string;
      }>('/api/patient/dependents/link', { method: 'POST', body: JSON.stringify(data) }),
    list: () =>
      fetchPatientApi<Array<{
        dependentLinkId: string; dependentPatientId: string; dependentClinicId: string;
        clinicName?: string; name: string; relationshipType: string; status: string; isMinor: boolean;
        activeConsentStatus?: string; activeConsentMethod?: string; validUntil?: string; createdAt: string;
      }>>('/api/patient/dependents'),
    unlink: (dependentLinkId: string, reason?: string) =>
      fetchPatientApi<void>(
        `/api/patient/dependents/${dependentLinkId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`,
        { method: 'DELETE' }
      ),
    guardianConsent: (dependentLinkId: string, data: {
      guardianAttestedName: string; guardianAttestedRelationship: string;
      guardianSignatureDataUrl?: string; guardianDobConfirmed: string; acceptedAttestation: boolean;
    }) =>
      fetchPatientApi<{ dependentConsentId: string; status: string; validFrom: string }>(
        `/api/patient/dependents/${dependentLinkId}/consent/guardian`,
        { method: 'POST', body: JSON.stringify(data) }
      ),
    sendOtp: (dependentLinkId: string) =>
      fetchPatientApi<{ dependentConsentId: string; status: string; maskedMobile: string; otpExpiresInSeconds: number }>(
        `/api/patient/dependents/${dependentLinkId}/consent/otp/send`,
        { method: 'POST' }
      ),
    verifyOtp: (dependentConsentId: string, otp: string) =>
      fetchPatientApi<{ error?: string; attemptsRemaining?: number; status?: string; validUntil?: string }>(
        '/api/patient/dependents/consent/otp/verify',
        { method: 'POST', body: JSON.stringify({ dependentConsentId, otp }) }
      ),
    revoke: (dependentConsentId: string, reason?: string) =>
      fetchPatientApi<void>(`/api/patient/dependents/consents/${dependentConsentId}/revoke`, {
        method: 'POST', body: JSON.stringify({ reason }),
      }),
  },

  records: {
    getVisits: () => fetchPatientApi<PatientVisitSummary[]>('/api/patient/visits'),
    getPrescriptions: () => fetchPatientApi<PatientPrescriptionSummary[]>('/api/patient/prescriptions'),
    getLabTrends: () => fetchPatientApi<Record<string, any[]>>('/api/patient/lab-trends'),
    getLabReports: () => fetchPatientApi<any[]>('/api/patient/lab-reports'),
    getCaseBrief: () => fetchPatientApi<string>('/api/patient/case-brief'),
    getLabOrders: () => fetchPatientApi<any[]>('/api/patient/lab-orders'),

    /**
     * The patient's lab results with plain-language AI interpretation.
     *
     * Deliberately carries no confidence figure, no clinician follow-up and no clinical-register
     * summary — the server does not send them. `summaryAvailable` is false until a clinician has
     * signed the interpretation off; render the awaiting-review state rather than any AI text.
     */
    getLabAiSummaries: () =>
      fetchPatientApi<PatientLabAiSummary[]>('/api/patient/lab-ai-summaries'),
    getLabUploads: () => fetchPatientApi<any[]>('/api/patient/lab-uploads'),
    getLabDocumentPreview: (docId: string) =>
      fetchPatientApi<{ url: string }>(`/api/patient/lab-documents/${docId}/preview`),
  },
};

export const templatesModule = {
  list: (category?: string, doctorId?: string) =>
    fetchApi<PrescriptionTemplate[]>(
      `/api/doctor/prescription-templates${buildQS({ category, doctorId })}`
    ),
  get: (id: string) =>
    fetchApi<PrescriptionTemplate>(`/api/doctor/prescription-templates/${id}`),
  create: (data: CreateTemplateRequest) =>
    fetchApi<PrescriptionTemplate>('/api/doctor/prescription-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<void>(`/api/doctor/prescription-templates/${id}`, { method: 'DELETE' }),
};

const BASE_ENH = '/api/doctor/templates/enhancements';

export const templateEnhancementsModule = {
  getFavorites: () =>
    fetchApi<PrescriptionTemplate[]>(`${BASE_ENH}/favorites`),
  getRecent: (days = 30) =>
    fetchApi<any[]>(`${BASE_ENH}/recent?days=${days}`),
  toggleFavorite: (templateId: string, favorite: boolean, pinned = false, pinOrder?: number) =>
    fetchApi<void>(`${BASE_ENH}/${templateId}/favorite`, {
      method: 'POST',
      body: JSON.stringify({ favorite, pinned, pinOrder }),
    }),
  recordUsage: (templateId: string) =>
    fetchApi<void>(`${BASE_ENH}/${templateId}/use`, { method: 'POST' }),
};

export const favoritesModule = {
  list: () => fetchApi<FavoriteMedication[]>('/api/doctor/favorite-medications'),
  top: (limit = 10) =>
    fetchApi<FavoriteMedication[]>(`/api/doctor/favorite-medications/top?limit=${limit}`),
  add: (data: AddFavoriteMedicationRequest) =>
    fetchApi<FavoriteMedication>('/api/doctor/favorite-medications', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  remove: (id: string) =>
    fetchApi<void>(`/api/doctor/favorite-medications/${id}`, { method: 'DELETE' }),
  incrementUse: (id: string) =>
    fetchApi<void>(`/api/doctor/favorite-medications/${id}/use`, { method: 'POST' }),
};

export const diagnosesModule = {
  list: () => fetchApi<CommonDiagnosis[]>('/api/common-diagnoses'),
  search: (query: string) =>
    fetchApi<CommonDiagnosis[]>(`/api/common-diagnoses/search?q=${encodeURIComponent(query)}`),
  byCategory: (category: string) =>
    fetchApi<CommonDiagnosis[]>(`/api/common-diagnoses/category/${category}`),
};

export const createEnhancedPrescription = (data: EnhancedPrescriptionCreateRequest) =>
  fetchApi<Prescription>('/api/doctor/prescriptions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
